import React, { useState } from 'react';
import { X, AlertCircle, Send, CheckCircle } from 'lucide-react';
import { supabase } from '../supabase/client';

interface QuestionData {
  id: string;
  questionTitle?: string;            // 题目名称（非客观题专用，管理用）
  questionStem?: string | null;      // 题干内容（富文本，包含解析的试卷题干）
  title?: string | null;            // 题干HTML或文本（保留用于向后兼容）
  content?: string | null;          // 兼容字段
  order_index?: number | null;      // 题号
  question_type?: string | null;    // 题型
  options?: any[] | null;           // 选项数组
  reference_answer?: string | null; // 参考答案
  explanation?: string | null;      // 解析
  scoring_points?: string | null;   // 评分规则
  title_images?: string[] | null;   // 题干图片
  title_image_widths?: number[] | null;
  title_image_layout?: string | null;
  image_position?: string | null;
  option_layout?: string | null;
}

interface ReportErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  questionId: string;
  questionTitle: string;
  paperId?: string;
  currentUser?: any;
  question?: QuestionData | null;   // 完整题目数据（可选）
}

const ERROR_TYPES = [
  { value: 'answer_error', label: '答案错误' },
  { value: 'question_error', label: '题目有误' },
  { value: 'option_error', label: '选项有误' },
  { value: 'explanation_error', label: '解析有误' },
  { value: 'other', label: '其他问题' },
];

// 判断是否为客观题（有选项的题目）
const isObjectiveType = (questionType?: string | null) => {
  return ['single_choice', 'multi_choice', 'multiple_choice', 'judge'].includes(questionType || '');
};

export default function ReportErrorModal({
  isOpen,
  onClose,
  questionId,
  questionTitle,
  paperId,
  currentUser,
  question,
}: ReportErrorModalProps) {
  const [errorType, setErrorType] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{ success: boolean; message: string } | null>(null);

  // 根据题型过滤纠错类型：非客观题移除"选项有误"
  const availableErrorTypes = isObjectiveType(question?.question_type)
    ? ERROR_TYPES
    : ERROR_TYPES.filter(t => t.value !== 'option_error');

  const handleSubmit = async () => {
    if (!errorType) {
      alert('请选择纠错类型');
      return;
    }
    if (!description.trim()) {
      alert('请输入纠错说明');
      return;
    }

    setIsSubmitting(true);
    try {
      // 获取用户角色
      const { data: userProfile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', currentUser?.id)
        .maybeSingle();

      const userRole = userProfile?.role || 'student';

      // 写入 error_corrections 表（统一数据源）
      const { data: correction, error: insertError } = await supabase
        .from('error_corrections')
        .insert({
          submitter_id: currentUser?.id || null,
          submitter_role: userRole,
          target_type: 'question',
          target_id: questionId,
          target_detail: paperId ? { paper_id: paperId } : null,
          error_description: description.trim(),
          suggested_correction: null,
          evidence_images: null,
          severity: 'medium',
          status: 'pending',
          source: 'manual',
          question_content: null,
          question_options: null,
          paper_title: questionTitle || null,
          error_type: errorType,
          resolution_note: null,
        })
        .select()
        .single();

      if (insertError) {
        throw new Error(insertError.message || '保存纠错记录失败');
      }

      setSubmitResult({ success: true, message: '纠错提交成功，感谢您的反馈！' });
      setTimeout(() => {
        onClose();
        setSubmitResult(null);
        setErrorType('');
        setDescription('');
      }, 2000);
    } catch (error) {
      console.error('提交纠错失败:', error);
      setSubmitResult({
        success: false,
        message: error instanceof Error ? error.message : '提交失败，请稍后重试',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setSubmitResult(null);
      setErrorType('');
      setDescription('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-500" />
            <h2 className="text-lg font-semibold text-gray-900">题目纠错</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-5">
          {/* Paper Info - 试卷信息 */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
            <div className="space-y-2">
              <div>
                <span className="text-xs text-blue-600 font-medium">试卷名称</span>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{questionTitle}</p>
              </div>
              {question && (
                <>
                  <div className="flex gap-4">
                    <div>
                      <span className="text-xs text-blue-600 font-medium">题目类型</span>
                      <p className="text-sm text-gray-700 mt-0.5">
                        {question.question_type === 'single_choice' ? '单选题' :
                         question.question_type === 'multi_choice' || question.question_type === 'multiple_choice' ? '多选题' :
                         question.question_type === 'judge' ? '判断题' :
                         question.question_type === 'fill_blank' ? '填空题' :
                         question.question_type === 'short_answer' ? '简答题' :
                         question.question_type === 'programming' ? '编程题' :
                         question.question_type || '未知'}
                      </p>
                    </div>
                    {question.order_index != null && (
                      <div>
                        <span className="text-xs text-blue-600 font-medium">题号</span>
                        <p className="text-sm text-gray-700 mt-0.5">第 {question.order_index} 题</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Error Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              纠错类型 <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {availableErrorTypes.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setErrorType(type.value)}
                  disabled={isSubmitting}
                  className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                    errorType === type.value
                      ? 'bg-blue-50 border-blue-500 text-blue-700'
                      : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                  } disabled:opacity-50`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              纠错说明 <span className="text-red-500">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isSubmitting}
              placeholder="请详细描述您发现的问题..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none disabled:opacity-50"
            />
          </div>

          {/* Submit Result */}
          {submitResult && (
            <div
              className={`flex items-center gap-2 p-3 rounded-lg ${
                submitResult.success
                  ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-700'
              }`}
            >
              {submitResult.success ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <AlertCircle className="w-5 h-5" />
              )}
              <span className="text-sm">{submitResult.message}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors disabled:opacity-50"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !errorType || !description.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                提交中...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                提交纠错
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
